package com.eladfinish.bible.contest.ui.extensions

import androidx.compose.foundation.ScrollState
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.composed
import androidx.compose.ui.draw.drawWithContent
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.BlendMode
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.drawscope.DrawScope
import androidx.compose.ui.graphics.drawscope.inset
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.input.pointer.PointerEventPass
import androidx.compose.ui.input.pointer.PointerInputChange
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.platform.debugInspectorInfo
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.unit.Dp
import com.eladfinish.bible.contest.currentMilliseconds
import com.eladfinish.bible.contest.extensions.runIf
import kotlinx.coroutines.coroutineScope

fun Modifier.bottomFadeEdgeEffect(
    fadeHeight: Dp,
    shouldShow: Boolean = true,
    color: Color = Color.Black,
) = this
    .graphicsLayer { alpha = 0.99f }
    .drawWithContent {
        drawContent()
        if (shouldShow) {
            inset(
                top = size.height - fadeHeight.toPx(),
                bottom = 0f,
                left = 0f,
                right = 0f
            ) {
                drawRect(
                    brush = Brush.verticalGradient(
                        colors = listOf(color, Color.Transparent),
                    ),
                    blendMode = BlendMode.DstIn
                )
            }
        }
    }

//fun Modifier.offsetOnKeyboardAppear(extraHeightPx: Int) = composed {
//    clipToBounds()
//        .offset(y = WindowInsets.keyboardOffset(extraHeightPx = extraHeightPx))
//}

fun Modifier.interceptTouchEvents() =
    pointerInput(Unit) {
        awaitPointerEventScope {
            // we should wait for all new pointer events
            while (true) {
                awaitPointerEvent(pass = PointerEventPass.Initial)
                    .changes
                    .forEach(PointerInputChange::consume)
            }
        }
    }

fun Modifier.clickableWithoutRipple(
    enabled: Boolean = true,
    onClickLabel: String? = null,
    role: Role? = null,
    onClick: () -> Unit,
) = composed {
    clickable(
        enabled = enabled,
        onClickLabel = onClickLabel,
        role = role,
        onClick = onClick,
        indication = null,
        interactionSource = remember { MutableInteractionSource() }
    )
}

@Composable
fun Modifier.conditional(
    condition: Boolean,
    modifier: @Composable Modifier.() -> Modifier,
): Modifier {
    return if (condition) {
        then(modifier(Modifier))
    } else {
        this
    }
}

fun <T> Modifier.ifNotNullThen(
    obj: T?,
    modifier: Modifier.(T) -> Modifier,
): Modifier {
    return if (obj != null) {
        then(modifier(obj))
    } else {
        this
    }
}

inline fun <T : Any> Modifier.modifyIfNotNull(
    data: T?,
    onSuccess: Modifier.(T) -> Modifier,
): Modifier = data?.let { value -> this.then(onSuccess(value)) } ?: this

fun Modifier.disableSplitMotionEvents() =
    pointerInput(Unit) {
        coroutineScope {
            var currentId: Long = -1L
            awaitPointerEventScope {
                while (true) {
                    awaitPointerEvent(PointerEventPass.Initial).changes.forEach { pointerInfo ->
                        when {
                            pointerInfo.pressed && currentId == -1L -> currentId = pointerInfo.id.value
                            pointerInfo.pressed.not() && currentId == pointerInfo.id.value -> currentId = -1
                            pointerInfo.id.value != currentId && currentId != -1L -> pointerInfo.consume()
                            else -> Unit
                        }
                    }
                }
            }
        }
    }

inline fun Modifier.debounceClickable(
    debounceInterval: Long = 400,
    crossinline onClick: () -> Unit,
): Modifier = composed {
    var lastClickTime by remember { mutableStateOf(0L) }
    clickable {
        val currentTime = currentMilliseconds
        if ((currentTime - lastClickTime) < debounceInterval) return@clickable
        lastClickTime = currentTime
        onClick()
    }
}

fun Modifier.horizontalFadingEdge(
    scrollState: ScrollState,
    length: Dp,
    edgeColor: Color? = null,
) = composed(
    debugInspectorInfo {
        name = "length"
        value = length
    }
) {
    val color = edgeColor ?: MaterialTheme.colorScheme.surface

    graphicsLayer { alpha = 0.99f }
        .drawWithContent {
            val lengthValue = length.toPx()

            val scrollFromStart by derivedStateOf { scrollState.maxValue - scrollState.value }
            val scrollFromEnd by derivedStateOf { scrollState.value }

            val startFadingEdgeStrength = lengthValue * (scrollFromStart / lengthValue).coerceAtMost(1f)

            val endFadingEdgeStrength = lengthValue * (scrollFromEnd / lengthValue).coerceAtMost(1f)

            drawContent()

            drawRect(
                brush = Brush.horizontalGradient(
                    colors = listOf(
                        Color.Transparent,
                        color,
                    ),
                    startX = 0f,
                    endX = startFadingEdgeStrength,
                ),
                size = Size(
                    width = startFadingEdgeStrength,
                    height = this.size.height,
                ),
                blendMode = BlendMode.DstIn,
            )

            drawRect(
                brush = Brush.horizontalGradient(
                    colors = listOf(
                        color,
                        Color.Transparent,
                    ),
                    startX = size.width - endFadingEdgeStrength,
                    endX = size.width,
                ),
                topLeft = Offset(x = size.width - endFadingEdgeStrength, y = 0f),
                blendMode = BlendMode.DstIn,
            )
        }
}


fun Modifier.horizontalFadeEdgeEffect(
    fadeWidth: Dp,
    shouldShow: Boolean = true,
    color: Color = Color.Black,
) = this
    .graphicsLayer { alpha = 0.99f }
    .drawWithContent {
        drawContent()
        if (shouldShow) {
            inset(
                top = 0f,
                bottom = 0f,
                left = size.height - fadeWidth.toPx(),
                right = size.height - fadeWidth.toPx(),
            ) {
                drawRect(
                    brush = Brush.verticalGradient(
                        colors = listOf(color, Color.Transparent),
                    ),
                    blendMode = BlendMode.DstIn
                )
            }
        }
    }

fun Modifier.verticalEdgesFadeEffect(
    fadeHeight: Dp,
    shouldShow: Boolean = true,
    color: Color = Color.Black,
) = verticalEdgesFadeEffect(
    fadeHeight = fadeHeight,
    showTopFade = shouldShow,
    showBottomFade = shouldShow,
    color = color,
)

/**
 * Draws a vertical fade gradient on whichever edges need it.
 *
 * Use [showTopFade] / [showBottomFade] to tie each edge to the scroll position:
 * top fade → `lazyListState.canScrollBackward`, bottom → `canScrollForward`.
 */
fun Modifier.verticalEdgesFadeEffect(
    fadeHeight: Dp,
    showTopFade: Boolean,
    showBottomFade: Boolean,
    color: Color = Color.Black,
) = this
    .graphicsLayer { alpha = 0.99f }
    .drawWithContent {
        drawContent()
        if (showBottomFade) {
            inset(
                top = size.height - fadeHeight.toPx(),
                bottom = 0f,
                left = 0f,
                right = 0f
            ) {
                drawVerticalGradient(color = color)
            }
        }
        if (showTopFade) {
            inset(
                top = 0f,
                bottom = size.height - fadeHeight.toPx(),
                left = 0f,
                right = 0f
            ) {
                drawVerticalGradient(color = color, invert = true)
            }
        }
    }

private fun DrawScope.drawVerticalGradient(color: Color, invert: Boolean = false) {
    drawRect(
        brush = Brush.verticalGradient(
            colors = listOf(color, Color.Transparent).runIf(invert) { reversed() },
        ),
        blendMode = BlendMode.DstIn
    )
}
